// ── WhatsAppSettingsPage ───────────────────────────────────────────
//
// Manages connected WhatsApp Business Accounts.
// • Lists connected accounts with status, quality, token expiry.
// • "Connect WhatsApp" launches the Meta Embedded Signup popup.
// • Per-account actions: refresh token, disconnect, edit flags.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  ChevronUp,
  ExternalLink,
  Loader2,
  Phone,
  Plus,
  RefreshCw,
  Settings2,
  Shield,
  WifiOff,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import {
  listWhatsappAccounts,
  refreshWhatsappToken,
  disconnectWhatsappAccount,
  updateWhatsappAccount,
  completeOnboarding,
  getIntegrationSettings,
  type WhatsappAccountListItem,
  type IntegrationSettings,
} from '../api/whatsapp.api';
import { SelectField } from '@/components/ui/SelectField';

// ── Meta Embedded Signup types (injected by the FB JS SDK) ────────
declare global {
  interface Window {
    FB?: {
      login: (
        callback: (response: { authResponse?: { code: string } | null }) => void,
        options: { config_id: string; response_type: string; override_default_response_type: boolean },
      ) => void;
      init: (params: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void;
    };
  }
}

// ── Quality badge ─────────────────────────────────────────────────

const QUALITY_BADGE: Record<string, string> = {
  GREEN: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  YELLOW: 'bg-amber-50 text-amber-700 border-amber-200',
  RED: 'bg-red-50 text-red-700 border-red-200',
  UNKNOWN: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_BADGE: Record<string, string> = {
  CONNECTED: 'bg-emerald-50 text-emerald-700',
  PENDING: 'bg-amber-50 text-amber-700',
  ERROR: 'bg-red-50 text-red-700',
  DISCONNECTED: 'bg-slate-100 text-slate-500',
};

// ── Onboarding modal (PIN + captured code → complete) ─────────────

function OnboardingModal({
  code,
  wabaId,
  phoneNumberId,
  onComplete,
  onCancel,
}: {
  code: string;
  wabaId: string;
  phoneNumberId: string;
  onComplete: () => void;
  onCancel: () => void;
}) {
  const [pin, setPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
      setError('PIN must be exactly 6 digits.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await completeOnboarding({ code, wabaId, phoneNumberId, pin });
      toast('success', 'WhatsApp account connected!');
      onComplete();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ??
        e?.response?.data?.error ??
        e?.message ??
        'Connection failed';
      setError(typeof msg === 'string' ? msg : 'Connection failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <Shield className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Set Two-Step Verification PIN</h2>
            <p className="text-xs text-slate-500">Required by Meta to secure your number</p>
          </div>
        </div>

        <p className="mb-4 text-sm text-slate-600">
          Choose a 6-digit PIN for two-step verification. Meta requires this to register your
          WhatsApp number. Store it safely — you may need it if you re-register later.
        </p>

        <div className="mb-1 flex gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`h-10 w-10 flex-shrink-0 rounded-md border-2 text-center text-lg font-mono font-semibold leading-[2.5rem] transition-colors ${
                i < pin.length
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 bg-white text-transparent'
              }`}
            >
              {i < pin.length ? '●' : ''}
            </div>
          ))}
        </div>
        <input
          type="tel"
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => e.key === 'Enter' && pin.length === 6 && submit()}
          className="mb-3 w-full rounded-md border border-gray-200 px-3 py-2 text-center font-mono text-lg tracking-[0.5em] focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          placeholder="000000"
          autoFocus
        />

        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 rounded-md border border-gray-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={pin.length !== 6 || saving}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-slate-300"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Connect Account
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Account settings drawer ───────────────────────────────────────

function AccountSettingsDrawer({
  account,
  onClose,
  onSaved,
}: {
  account: WhatsappAccountListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [autoAssign, setAutoAssign] = useState(true);
  const [sessionHours, setSessionHours] = useState(24);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateWhatsappAccount(account.id, {
        autoAssignEnabled: autoAssign,
        sessionWindowHours: sessionHours,
      });
      toast('success', 'Account settings updated');
      onSaved();
    } catch {
      toast('error', 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30 p-4">
      <div className="mt-16 w-full max-w-sm rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-800">Account Settings</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <ChevronUp className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-5 px-5 py-4">
          <div>
            <p className="text-xs font-medium text-slate-500">Phone number</p>
            <p className="text-sm font-semibold text-slate-800">{account.displayPhoneNumber}</p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Auto-assign conversations</p>
              <p className="text-xs text-slate-400">Assign new inbound chats to available agents</p>
            </div>
            <button
              onClick={() => setAutoAssign((v) => !v)}
              className={`relative h-6 w-11 rounded-full transition-colors ${
                autoAssign ? 'bg-indigo-500' : 'bg-slate-200'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  autoAssign ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>
          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-700">
              Session window override (hours)
            </p>
            <SelectField
              value={sessionHours}
              onChange={(e) => setSessionHours(Number(e.target.value))}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
            >
              {[12, 24, 48, 72].map((h) => (
                <option key={h} value={h}>
                  {h}h
                </option>
              ))}
            </SelectField>
            <p className="mt-1 text-xs text-slate-400">
              Meta enforces 24h. Set higher only if your plan allows extended windows.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-200 px-4 py-1.5 text-sm text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-slate-300"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Account card ──────────────────────────────────────────────────

function AccountCard({
  account,
  onRefresh,
  onDisconnect,
  onSettings,
}: {
  account: WhatsappAccountListItem;
  onRefresh: () => void;
  onDisconnect: (id: string, name: string) => void;
  onSettings: (account: WhatsappAccountListItem) => void;
}) {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshWhatsappToken(account.id);
      toast('success', 'Token refreshed');
      onRefresh();
    } catch (e: any) {
      toast('error', e?.response?.data?.message ?? 'Failed to refresh token');
    } finally {
      setRefreshing(false);
    }
  };

  const statusClass = STATUS_BADGE[account.status] ?? 'bg-slate-100 text-slate-500';
  const qualityClass =
    QUALITY_BADGE[account.qualityRating] ?? QUALITY_BADGE.UNKNOWN;

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50">
            <Phone className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">{account.displayPhoneNumber}</p>
            {account.verifiedName && (
              <p className="text-xs text-slate-500">{account.verifiedName}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClass}`}
          >
            {account.status}
          </span>
          <span
            className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${qualityClass}`}
          >
            {account.qualityRating}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          {refreshing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh token
        </button>
        <button
          onClick={() => onSettings(account)}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] font-medium text-slate-600 hover:bg-slate-50"
        >
          <Settings2 className="h-3.5 w-3.5" />
          Settings
        </button>
        <button
          onClick={() => onDisconnect(account.id, account.displayPhoneNumber)}
          className="inline-flex items-center gap-1.5 rounded-md border border-red-100 bg-red-50 px-2.5 py-1.5 text-[12px] font-medium text-red-600 hover:bg-red-100"
        >
          <WifiOff className="h-3.5 w-3.5" />
          Disconnect
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────

export default function WhatsAppSettingsPage() {
  const [accounts, setAccounts] = useState<WhatsappAccountListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [pendingOnboarding, setPendingOnboarding] = useState<{
    code: string;
    wabaId: string;
    phoneNumberId: string;
  } | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [settingsAccount, setSettingsAccount] =
    useState<WhatsappAccountListItem | null>(null);
  const sdkScriptRef = useRef<HTMLScriptElement | null>(null);

  // Integration settings — fetched to drive FB SDK init; editing is in Settings → Integrations
  const [integSettings, setIntegSettings] = useState<IntegrationSettings | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listWhatsappAccounts()
      .then(setAccounts)
      .catch(() => toast('error', 'Failed to load accounts'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Fetch integration settings on mount — drives FB SDK init only
  useEffect(() => {
    getIntegrationSettings()
      .then(setIntegSettings)
      .catch(() => {/* non-fatal */});
  }, []);

  // Inject the FB JS SDK when integration settings are loaded.
  useEffect(() => {
    const appId = integSettings?.metaAppId;
    if (!appId) return;
    const existing = document.getElementById('facebook-jssdk');
    if (existing) {
      // SDK already present — just (re-)init with current appId
      window.FB?.init({ appId, cookie: true, xfbml: true, version: 'v21.0' });
      setSdkLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.FB?.init({ appId, cookie: true, xfbml: true, version: 'v21.0' });
      setSdkLoaded(true);
    };
    document.body.appendChild(script);
    sdkScriptRef.current = script;
    return () => {
      // leave it in the DOM across navigations
    };
  }, [integSettings?.metaAppId]);

  const launchSignup = () => {
    const configId = integSettings?.metaConfigId;
    if (!window.FB || !configId) {
      toast('error', 'Meta SDK not ready. Configure your Meta App ID and Config ID in the Integration Settings below.');
      return;
    }
    setLaunching(true);
    window.FB.login(
      (response) => {
        setLaunching(false);
        if (!response.authResponse?.code) {
          toast('error', 'Meta signup cancelled or failed.');
          return;
        }
        // The Meta popup returns the code + wabaId + phoneNumberId
        // in the authResponse. For Embedded Signup the params are
        // available in the authResponse.data or through the messaging
        // widget events. For simplicity we use a dialog approach:
        // the code is in authResponse.code; wabaId / phoneNumberId
        // are passed back via a listener on the FB.login callback
        // in the data field. If not present we prompt the user.
        const data: any = (response as any)?.authResponse?.data_access_expiration_time
          ? (response as any).authResponse
          : {};
        // In the embedded signup flow, wabaId and phoneNumberId come
        // back in the `data` param from the FB event. For now we
        // show a manual-entry fallback (common in tech-provider flows).
        const wabaId: string = data?.waba_id ?? '';
        const phoneNumberId: string = data?.phone_number_id ?? '';
        if (!wabaId || !phoneNumberId) {
          // Prompt user to enter the IDs manually
          const waba = window.prompt('Enter your WhatsApp Business Account ID (numeric):') ?? '';
          const phone = window.prompt('Enter your Phone Number ID (numeric):') ?? '';
          if (!waba || !phone) {
            toast('error', 'WABA ID and Phone Number ID are required.');
            return;
          }
          setPendingOnboarding({ code: response.authResponse!.code, wabaId: waba, phoneNumberId: phone });
        } else {
          setPendingOnboarding({
            code: response.authResponse!.code,
            wabaId,
            phoneNumberId,
          });
        }
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
      },
    );
  };

  const handleDisconnect = async () => {
    if (!disconnectTarget) return;
    try {
      await disconnectWhatsappAccount(disconnectTarget.id);
      toast('success', 'Account disconnected');
      setDisconnectTarget(null);
      load();
    } catch {
      toast('error', 'Failed to disconnect account');
    }
  };

  const sdkMissing = !integSettings?.metaAppId || !integSettings?.metaConfigId;

  return (
    <DashboardLayout title="WhatsApp Settings">
      <div className="mx-auto max-w-3xl space-y-6 px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">WhatsApp Accounts</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Connect your WhatsApp Business numbers via Meta Embedded Signup.
            </p>
          </div>
          {!sdkMissing && (
            <button
              onClick={launchSignup}
              disabled={launching || !sdkLoaded}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:bg-slate-300"
            >
              {launching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Connect WhatsApp
            </button>
          )}
        </div>

        {/* Missing credentials warning */}
        {sdkMissing && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <div>
              <p className="font-medium">Meta App not configured</p>
              <p className="mt-0.5 text-amber-700">
                Go to <strong>Settings → Integrations → WhatsApp Business</strong> and fill in your{' '}
                <strong>Meta App ID</strong>, <strong>Meta App Secret</strong>, and{' '}
                <strong>Meta Config ID</strong> to enable the Embedded Signup popup.{' '}
                <a
                  href="https://developers.facebook.com/docs/whatsapp/embedded-signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 font-medium underline"
                >
                  Meta docs <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>
          </div>
        )}

        {/* Account list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
              <Phone className="h-7 w-7 text-green-500" />
            </div>
            <p className="text-sm font-medium text-slate-700">No WhatsApp numbers connected yet</p>
            <p className="mt-1 text-xs text-slate-400">
              Click "Connect WhatsApp" to link your first WhatsApp Business number.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map((acc) => (
              <AccountCard
                key={acc.id}
                account={acc}
                onRefresh={load}
                onDisconnect={(id, name) => setDisconnectTarget({ id, name })}
                onSettings={(a) => setSettingsAccount(a)}
              />
            ))}
          </div>
        )}

        {/* How it works */}
        <div className="rounded-xl border border-gray-100 bg-slate-50 p-5">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
            How it works
          </p>
          <ol className="space-y-2 text-sm text-slate-600">
            {[
              'Click "Connect WhatsApp" — a Meta popup opens in your browser.',
              'Log in with your Facebook/Meta Business account and select your WABA.',
              'Choose the phone number to register with BrixLead.',
              'Set a 6-digit two-step verification PIN when prompted.',
              'BrixLead exchanges the token and subscribes to your webhook automatically.',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-semibold text-indigo-600">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>

      </div>

      {/* Disconnect confirm modal */}
      {disconnectTarget && (
        <ConfirmModal
          title="Disconnect WhatsApp number"
          message={`Disconnect ${disconnectTarget.name}? Existing conversations are preserved but no new messages can be sent or received until you reconnect.`}
          confirmText="Disconnect" isOpen={true}
          onConfirm={handleDisconnect}
          onCancel={() => setDisconnectTarget(null)}
        />
      )}

      {/* Onboarding (PIN entry) modal */}
      {pendingOnboarding && (
        <OnboardingModal
          {...pendingOnboarding}
          onComplete={() => {
            setPendingOnboarding(null);
            load();
          }}
          onCancel={() => setPendingOnboarding(null)}
        />
      )}

      {/* Account settings drawer */}
      {settingsAccount && (
        <AccountSettingsDrawer
          account={settingsAccount}
          onClose={() => setSettingsAccount(null)}
          onSaved={() => {
            setSettingsAccount(null);
            load();
          }}
        />
      )}
    </DashboardLayout>
  );
}
